"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Building2,
  Home,
  Hotel,
  Landmark,
  Tent,
  Warehouse,
} from "lucide-react";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { useJourneyData } from "@/hooks/use-journey-data";
import { PROPERTY_TYPE_LABELS, type PropertyType } from "@/schemas/journey";
import { cn } from "@masterkey/ui/cn";

const PROPERTY_TYPE_CONFIG = {
  house: {
    icon: Home,
    description:
      "A standalone residential building on its own lot, offering the most privacy and control over property decisions.",
  },
  townhome: {
    icon: Building2,
    description:
      "An attached home sharing walls with neighbors, often part of an HOA but with more independence than a condo.",
  },
  multi_family: {
    icon: Warehouse,
    description:
      "A property with multiple separate living units, suitable for both living in and generating rental income.",
  },
  condo: {
    icon: Hotel,
    description:
      "An individually owned unit within a larger building or complex, with shared amenities and HOA management.",
  },
  land: {
    icon: Landmark,
    description:
      "Undeveloped property that can be built on according to local zoning laws.",
  },
  apartment: {
    icon: Building2,
    description:
      "A rental unit within a larger residential building or complex.",
  },
  manufactured: {
    icon: Tent,
    description:
      "A factory-built home that can be placed on owned or leased land.",
  },
} as const;

const BEDROOM_OPTIONS = ["Any", "1+", "2+", "3+", "4+", "5+"] as const;
const BATHROOM_OPTIONS = ["Any", "1+", "1.5+", "2+", "3+", "4+"] as const;

const propertyPreferencesSchema = z.object({
  propertyTypes: z
    .array(z.custom<PropertyType>())
    .min(1, "Please select at least one property type"),
  minBedrooms: z.string(),
  minBathrooms: z.string(),
});

type PropertyPreferencesForm = z.infer<typeof propertyPreferencesSchema>;

export function PropertyPreferencesForm() {
  const { journeyData, updateJourney } = useJourneyData();
  const { toast } = useToast();
  const updateTimeoutRef = useRef<NodeJS.Timeout>();

  const form = useForm<PropertyPreferencesForm>({
    resolver: zodResolver(propertyPreferencesSchema),
    values: {
      propertyTypes: journeyData?.propertyTypes || [],
      minBedrooms: journeyData?.minBedrooms
        ? `${journeyData.minBedrooms}+`
        : "Any",
      minBathrooms: journeyData?.minBathrooms
        ? `${journeyData.minBathrooms}+`
        : "Any",
    },
  });

  const handleUpdate = (values: PropertyPreferencesForm) => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = setTimeout(() => {
      const data = {
        propertyTypes: values.propertyTypes,
        minBedrooms:
          values.minBedrooms === "Any"
            ? 0
            : Number.parseInt(values.minBedrooms),
        minBathrooms:
          values.minBathrooms === "Any"
            ? 0
            : Number.parseFloat(values.minBathrooms),
      };

      updateJourney.mutate(data, {
        onSuccess: () => {
          toast({
            title: "Preferences Updated",
            description: "Your property preferences have been saved",
          });
        },
      });
    }, 500);
  };

  return (
    <Form {...form}>
      <form className="space-y-12 max-w-3xl mx-auto">
        <FormField
          control={form.control}
          name="propertyTypes"
          render={({ field }) => (
            <FormItem className="space-y-4">
              <div className="text-sm font-semibold mb-4">Property Types</div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <TooltipProvider>
                  {Object.entries(PROPERTY_TYPE_LABELS).map(
                    ([type, label]: [PropertyType, string]) => {
                      const config =
                        PROPERTY_TYPE_CONFIG[
                          type as keyof typeof PROPERTY_TYPE_CONFIG
                        ];

                      if (!config) {
                        console.error(
                          `Missing config for property type: ${type}`,
                        );
                        return null; // Skip rendering if config is missing
                      }

                      const IconComponent = config.icon;
                      const description = config.description;

                      return (
                        <Tooltip key={type}>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              className={cn(
                                "h-auto py-4 px-3",
                                field.value.includes(type) &&
                                  "border-primary text-primary bg-primary/5 hover:bg-primary/10",
                              )}
                              onClick={() => {
                                const newValue = field.value.includes(
                                  type as PropertyType,
                                )
                                  ? field.value.filter(
                                      (t) => t !== (type as PropertyType),
                                    )
                                  : [...field.value, type as PropertyType];
                                field.onChange(newValue);
                                handleUpdate({
                                  ...form.getValues(),
                                  propertyTypes: newValue as PropertyType[],
                                });
                              }}
                            >
                              <div className="flex flex-col items-center gap-2">
                                <IconComponent className="w-5 h-5" />
                                <span className="text-sm">{label}</span>
                              </div>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            <p>{description}</p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    },
                  )}
                </TooltipProvider>
              </div>
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField
            control={form.control}
            name="minBedrooms"
            render={({ field }) => (
              <FormItem>
                <div className="text-sm font-semibold mb-4">
                  Minimum Bedrooms
                </div>
                <FormControl>
                  <div className="flex flex-wrap gap-2">
                    {BEDROOM_OPTIONS.map((option) => (
                      <Button
                        key={option}
                        type="button"
                        variant="outline"
                        className={cn(
                          "flex-1 min-w-[4rem]",
                          field.value === option &&
                            "border-primary text-primary bg-primary/5 hover:bg-primary/10",
                        )}
                        onClick={() => {
                          field.onChange(option);
                          handleUpdate({
                            ...form.getValues(),
                            minBedrooms: option,
                          });
                        }}
                      >
                        {option}
                      </Button>
                    ))}
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="minBathrooms"
            render={({ field }) => (
              <FormItem>
                <div className="text-sm font-semibold mb-4">
                  Minimum Bathrooms
                </div>
                <FormControl>
                  <div className="flex flex-wrap gap-2">
                    {BATHROOM_OPTIONS.map((option) => (
                      <Button
                        key={option}
                        type="button"
                        variant="outline"
                        className={cn(
                          "flex-1 min-w-[4rem]",
                          field.value === option &&
                            "border-primary text-primary bg-primary/5 hover:bg-primary/10",
                        )}
                        onClick={() => {
                          field.onChange(option);
                          handleUpdate({
                            ...form.getValues(),
                            minBathrooms: option,
                          });
                        }}
                      >
                        {option}
                      </Button>
                    ))}
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  );
}
